import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { useAuth } from '@langmail/shared-ui'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/ui/logo'

const MIN_PASSWORD_LENGTH = 8

export const ResetPassword = () => {
  const { resetPassword } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Better Auth's GET /reset-password/:token redirects to this page with the
  // token appended (also surfaces `?error=INVALID_TOKEN` for a bad/expired link).
  const token = searchParams.get('token')
  const linkError = searchParams.get('error')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!token) {
      return
    }

    setSubmitting(true)
    const { error: resetError } = await resetPassword({ token, newPassword: password })
    if (resetError) {
      setError(resetError.message)
      setSubmitting(false)
      return
    }
    navigate('/login?reset=success')
  }

  return (
    <div className="flex h-dvh w-dvw flex-col items-center justify-center bg-background">
      <div className="w-full max-w-[400px] px-10 text-center">
        <Logo />

        <div className="mt-16">
          <h1 className="mb-3 font-semibold text-2xl text-foreground">Choose a new password</h1>

          {!token || linkError ? (
            <div className="mt-6">
              <p className="text-muted-foreground text-sm">
                This reset link is invalid or has expired. Please request a new one.
              </p>
              <p className="mt-6 text-muted-foreground text-sm">
                <Link className="underline hover:text-foreground" to="/forgot-password">
                  Request a new link
                </Link>
              </p>
            </div>
          ) : (
            <form className="mt-6 space-y-4 text-left" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  autoComplete="new-password"
                  id="password"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 8 characters"
                  type="password"
                  value={password}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <Input
                  autoComplete="new-password"
                  id="confirmPassword"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  type="password"
                  value={confirmPassword}
                />
              </div>

              {error ? <p className="text-destructive text-sm">{error}</p> : null}

              <Button
                className="w-full rounded-lg bg-primary px-8 py-4 text-[15px] text-primary-foreground hover:bg-primary/90"
                disabled={submitting || password.length === 0 || confirmPassword.length === 0}
                type="submit"
              >
                {submitting ? 'Saving…' : 'Reset password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
